import * as React from 'react';
import {
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { TAG_TONE_COLORS } from 'components/constants/tagTones';
import {
  AlertTriangle,
  BarChart3,
  BookOpen,
  Bot,
  BotMessageSquare,
  Briefcase,
  CalendarDays,
  ChartColumn,
  ClipboardList,
  FileSignature,
  FileSpreadsheet,
  FileText,
  GraduationCap,
  Landmark,
  LayoutDashboard,
  LayoutGrid,
  Library,
  Lightbulb,
  Link2,
  ListChecks,
  LucideIcon,
  Magnet,
  Megaphone,
  MessageSquareText,
  MessagesSquare,
  PieChart,
  Receipt,
  Settings,
  Target,
  Telescope,
  TrendingUp,
  UserCheck,
  UserRound,
  Users,
  Video,
  Wallet,
} from 'lucide-react';

import { PATHS } from 'router/config';
import {
  AI_AGENT_ROLES,
  COMMUNICATOR_ROLES,
  TASK_TRACKER_ROLES,
  EDUCATION_SECTION_ROLES,
  ESIGN_PAGE_ROLES,
  FINANCE_EXPENSE_ROLES,
  LEAD_ROLES,
  MARKETING_ROLES,
  MCP_ACCESS_ROLES,
  PROFILE_ROLES,
  SETTINGS_HUB_ROLES,
  SHORT_LINK_ROLES,
  SMS_ROLES,
  TEACHER_PROFILE_ROLES,
  useEsignAvailability,
  useLogOut,
  useMyCommsCounters,
  useNotificationCenter,
  useNotificationUnreadCount,
  usePalette,
  useUser,
} from 'core/hooks';
import {
  aiKbShowsReviewEntry,
  AiKbReviewerMe,
} from 'core/hooks/AiKnowledge/aiKnowledgeModel';
import { formatAiCount } from 'core/hooks/AiAgents/aiAgentPlatformModel';
import { useAiKbReviewerMe } from 'core/hooks/AiKnowledge/useAiKbReview';
import { useIsMobile } from 'core/hooks/Layout';
import { UserRole } from 'types';

import { CommsCounters, MenuItem } from 'molecules';
import { NotificationCenter } from '../NotificationCenter';

import { MenuComponent } from './Menu';
import { MobileHeader } from './MobileHeader';
import { MobileMenuSheet } from './MobileMenuSheet';
import { MobileTabBar } from './MobileTabBar';
import { findActiveLeaf, getMobileTabs, isPathActive } from './mobileNav';

/** A single navigable page inside a top-level menu group. */
export interface IMenuLeaf {
  text: string;
  to: string;
  /** Path prefix used for active-state detection; defaults to `to`. */
  match?: string;
  roles: UserRole[];
  /**
   * Пункт живёт за флагом сервера. ТЗ 0076: «Договоры» не видны, пока
   * `ESIGN_ENABLED` выключен, — переходный период с PandaDoc. ТЗ 0118:
   * `kbReview` — очередь базы знаний видна рецензенту без Admin с
   * назначенными категориями (`aiKbReviewerMe`).
   */
  feature?: 'esign' | 'kbReview';
  /**
   * Значок пункта — в выпадающем списке и у пункта-одиночки в шапке.
   * Обязателен (решение владельца 2026-09-22: значок у КАЖДОГО пункта).
   * Где у страницы есть команда в палитре, значок тот же, что у команды
   * (`CommandPalette/commands.ts`): одна страница — один знак.
   */
  icon: LucideIcon;
}

/**
 * A top-level menu entry — renders as a dropdown when 2+ leaves are visible.
 *
 * Значка у группы нет намеренно: шесть групп с ним не помещаются в шапку
 * на 1280 px (замерено: +22 px на группу), а переносить подписи в две
 * строки нельзя.
 */
export interface IMenuGroup {
  text: string;
  children: IMenuLeaf[];
}

const STUDENT_ROLES = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
  UserRole.FINANCIER,
];

/**
 * Кто видит «Курсы» и «Группы» — `EDUCATION_SECTION_ROLES`, та же константа
 * у маршрута «Обучения», палитры и `g → …` (ТЗ 0093; до него у меню был свой
 * список с методистом, у палитры — без).
 *
 * 🔴 Методиста здесь не было — а программу курса и его навыки ведёт именно он
 * (`CAREER_DICTIONARY_WRITE_ROLES` на сервере = админ + методист). Попасть на
 * карточку курса он мог только набрав адрес руками. Добавлен.
 *
 * ⚠️ Остаётся вторая половина той же кривизны, и трогать её здесь нельзя:
 * у SalesManager пункт есть, а маршрута `courseDetail` в его наборе нет —
 * клик по строке курса даёт 404. Убрать пункт значит молча урезать доступ,
 * выдать маршрут — молча его расширить; и то и другое решает владелец, а не
 * правка вёрстки. Соседний комментарий про опросы фиксирует ту же беду. Так
 * же и «Преподаватели» у методиста: пункт есть, маршрута `teachers` нет.
 */
const EDUCATION_ROLES = EDUCATION_SECTION_ROLES;

/**
 * Меню «по задачам» (решение владельца 2026-09-22): студенты, продажи и
 * договоры, связь, обучение, финансы, система. Пункты переставлены между
 * группами как есть — роли, флаги и `match` у каждого прежние.
 */
const getMenuStructure = (t: TFunction): IMenuGroup[] => [
  {
    text: `${t('menu.students')}`,
    children: [
      {
        text: `${t('menu.dashboard')}`,
        to: PATHS.dashboard,
        roles: TASK_TRACKER_ROLES,
        icon: LayoutDashboard,
      },
      {
        text: `${t('menu.students')}`,
        to: PATHS.students,
        roles: STUDENT_ROLES,
        icon: Users,
      },
      {
        text: `${t('menu.debtors')}`,
        to: PATHS.debtors,
        roles: STUDENT_ROLES,
        icon: AlertTriangle,
      },
      {
        text: `${t('menu.performance')}`,
        to: PATHS.performance,
        roles: [UserRole.ADMIN, UserRole.ACCOUNTANT, UserRole.HRMANAGER],
        icon: BarChart3,
      },
      {
        // ТЗ 0024. Роли перечислены литералом, а не через STUDENT_ROLES:
        // список совпадает по составу, но это другое правило, и связывать их
        // одной константой значит менять оба, меняя одно.
        text: `${t('menu.dofinansowanie')}`,
        to: PATHS.dofinansowanie,
        roles: [
          UserRole.ADMIN,
          UserRole.ACCOUNTANT,
          UserRole.SALESMANAGER,
          UserRole.FINANCIER,
        ],
        icon: Landmark,
      },
    ],
  },
  {
    text: `${t('menu.salesContracts')}`,
    children: [
      {
        // ТЗ 0084: лиды Kommo — первым в продажах. Роли — `LEAD_ROLES`,
        // тот же список у маршрута и палитры.
        text: `${t('menu.leads')}`,
        to: PATHS.leads,
        roles: LEAD_ROLES,
        icon: Magnet,
      },
      {
        // ТЗ 0076. Роли — из `ESIGN_PAGE_ROLES` (зеркало `ESIGN_READ_ROLES`
        // бэкенда), а не STUDENT_ROLES: HR оформляет документы
        // преподавателей, а в STUDENT_ROLES его нет. Тот же список берут
        // маршрут и палитра — три места не имеют права разойтись.
        text: `${t('menu.contracts')}`,
        to: PATHS.contracts,
        roles: ESIGN_PAGE_ROLES,
        feature: 'esign',
        icon: FileSignature,
      },
      {
        text: `${t('menu.pandadoc')}`,
        to: PATHS.debtorsPandadoc,
        match: PATHS.debtorsPandadoc,
        roles: STUDENT_ROLES,
        // Тот же знак, что в заголовке страницы аналитики PandaDoc.
        icon: ChartColumn,
      },
      {
        text: `${t('menu.pandadocProvisioning')}`,
        to: PATHS.debtorsPandadocProvisioning,
        roles: STUDENT_ROLES,
        icon: UserCheck,
      },
      {
        // ТЗ 0088: маркетинг — внутри «Продаж», а не седьмой группой: шесть
        // групп и так впритык в шапке на 1280 px. Роли — `MARKETING_ROLES`,
        // тот же список у маршрута и палитры. У роли Marketing это
        // единственный пункт — он рисуется одиночкой со своим значком.
        text: `${t('menu.marketing')}`,
        to: PATHS.marketing,
        roles: MARKETING_ROLES,
        icon: Megaphone,
      },
    ],
  },
  {
    text: `${t('menu.communications')}`,
    children: [
      {
        text: `${t('menu.communicator')}`,
        to: PATHS.communicator,
        // Роли — `COMMUNICATOR_ROLES` (с HR), тот же список у маршрута и
        // палитры.
        roles: COMMUNICATOR_ROLES,
        icon: MessagesSquare,
      },
      {
        // ТЗ 0081: SMS студентам — журнал, настройки, лестницы, шаблоны.
        // Роли — `SMS_ROLES`, тот же список у маршрута и палитры.
        text: `${t('menu.sms')}`,
        to: PATHS.sms,
        roles: SMS_ROLES,
        icon: MessageSquareText,
      },
      {
        // ТЗ 0080: короткие ссылки. Без `feature`: сократитель от флага
        // подписи не зависит (ручные ссылки для рассылок живут и без неё).
        // Роли — `SHORT_LINK_ROLES`, тот же список у маршрута и палитры.
        text: `${t('menu.shortLinks')}`,
        to: PATHS.shortLinks,
        roles: SHORT_LINK_ROLES,
        icon: Link2,
      },
    ],
  },
  {
    text: `${t('menu.education')}`,
    children: [
      {
        text: `${t('menu.courses')}`,
        to: PATHS.settingsCourses,
        roles: EDUCATION_ROLES,
        icon: BookOpen,
      },
      {
        text: `${t('menu.groups')}`,
        to: PATHS.settingsGroups,
        roles: EDUCATION_ROLES,
        icon: LayoutGrid,
      },
      {
        text: `${t('menu.journals')}`,
        to: PATHS.settingsJournals,
        roles: EDUCATION_ROLES,
        icon: ClipboardList,
      },
      {
        text: `${t('menu.teachers')}`,
        to: PATHS.teachers,
        // Не «Обучение» целиком: преподавателей сервер отдаёт только
        // `TEACHER_PROFILE_ROLES` (роли карточки и методист, решение
        // владельца 09.10) — тот же список, что у маршрута и палитры.
        roles: TEACHER_PROFILE_ROLES,
        icon: GraduationCap,
      },
      {
        // Spec 0011: explicit SURVEY_MANAGER_ROLES — deliberately NOT
        // EDUCATION_ROLES (that list carries SalesManager/HR and lacks
        // Methodist, wrong in both directions for surveys).
        text: `${t('menu.surveys')}`,
        to: PATHS.surveys,
        roles: [UserRole.ADMIN, UserRole.ACCOUNTANT, UserRole.METHODIST],
        icon: ListChecks,
      },
      {
        // Spec 0038: the analytics tier is wider than the constructor tier —
        // HR reads the aggregates because HR owns the teacher conversation.
        // Without its own entry HR held two routes it could reach only by
        // typing the URL: no menu item, no palette command, no shortcut.
        text: `${t('menu.surveyAnalytics')}`,
        to: PATHS.surveyAnalytics,
        roles: [
          UserRole.ADMIN,
          UserRole.ACCOUNTANT,
          UserRole.METHODIST,
          UserRole.HRMANAGER,
        ],
        // Не BarChart3 — он у «Успеваемости» в палитре и в меню.
        icon: PieChart,
      },
      {
        // Spec 0098 «Обзор»: same analytics tier as above. Since the UX pack
        // 0095–0098 HR reads it in full — comments, students, the AI digest
        // (useSurveyPermissions.canReadInsights); only links to routes HR
        // lacks (survey results, the student card) render as text.
        text: `${t('menu.surveyInsights')}`,
        to: PATHS.surveyInsights,
        roles: [
          UserRole.ADMIN,
          UserRole.ACCOUNTANT,
          UserRole.METHODIST,
          UserRole.HRMANAGER,
        ],
        icon: Telescope,
      },
      {
        // Spec 0018 CAREER_ROLES — adds HRManager on top of the survey set;
        // must stay in sync with `paletteRoles.CAREER_ROLES` and
        // `AppRouter#getRoutesByRole`.
        text: `${t('menu.careerCenter')}`,
        to: PATHS.careerCenter,
        roles: [
          UserRole.ADMIN,
          UserRole.ACCOUNTANT,
          UserRole.HRMANAGER,
          UserRole.METHODIST,
        ],
        icon: Briefcase,
      },
      {
        // ТЗ 0040 доводка: до этого пункта справочники открывались ТОЛЬКО
        // набором адреса вручную — ни меню, ни палитра о них не знали.
        // Один пункт, а не шесть: внутри они связаны общей полосой переходов,
        // а меню и без того длинное.
        text: `${t('menu.careerDictionaries')}`,
        to: PATHS.careerSkills,
        match: '/dictionaries',
        roles: [
          UserRole.ADMIN,
          UserRole.ACCOUNTANT,
          UserRole.HRMANAGER,
          UserRole.METHODIST,
        ],
        // Не Briefcase палитры: он уже у «Карьерного центра» рядом.
        icon: Library,
      },
    ],
  },
  {
    text: `${t('menu.finances')}`,
    children: [
      {
        text: `${t('menu.financesDashboard')}`,
        to: PATHS.financesDashboard,
        match: PATHS.financesDashboard,
        roles: [
          UserRole.ADMIN,
          UserRole.FINANCIER,
          UserRole.ACCOUNTANT,
          UserRole.SALESMANAGER,
        ],
        icon: Wallet,
      },
      {
        text: `${t('menu.financesInvoices')}`,
        to: PATHS.financesInvoices,
        match: PATHS.financesInvoices,
        roles: [
          UserRole.ADMIN,
          UserRole.FINANCIER,
          UserRole.ACCOUNTANT,
          UserRole.SALESMANAGER,
        ],
        icon: FileText,
      },
      {
        text: `${t('menu.financesExpenses')}`,
        to: PATHS.financesExpenses,
        match: PATHS.financesExpenses,
        // Зеркало `EXPENSES_ACCESS_ROLES`: аккаунт-менеджеру расходы не
        // открыты (решение владельца 06.10.2026) — ни пунктом, ни маршрутом.
        roles: FINANCE_EXPENSE_ROLES,
        icon: Receipt,
      },
      {
        text: `${t('menu.financesPlan')}`,
        to: PATHS.finances,
        match: PATHS.finances,
        roles: [UserRole.ADMIN, UserRole.FINANCIER],
        icon: Target,
      },
      {
        text: `${t('menu.financesPaymentCalendar')}`,
        to: PATHS.financesPaymentCalendar,
        match: PATHS.financesPaymentCalendar,
        roles: [UserRole.ADMIN, UserRole.FINANCIER],
        icon: CalendarDays,
      },
      {
        text: `${t('menu.financesCashFlow')}`,
        to: PATHS.financesCashFlow,
        match: PATHS.financesCashFlow,
        roles: [UserRole.ADMIN, UserRole.FINANCIER],
        icon: TrendingUp,
      },
      {
        text: `${t('menu.financesPnl')}`,
        to: PATHS.financesPnl,
        match: PATHS.financesPnl,
        roles: [UserRole.ADMIN, UserRole.FINANCIER],
        icon: FileSpreadsheet,
      },
    ],
  },
  {
    // ТЗ 0093: была «Система». Первый пункт — хаб настроек: все разделы
    // конфигурации (робот блокировки, SMS, шаблоны, интеграции…) живут там,
    // а не отдельными пунктами — седьмой группе в шапке на 1280 px места нет.
    text: `${t('menu.settings')}`,
    children: [
      {
        // Активен и на самом хабе, и в любом его разделе `/settings/*`
        // (`isPathActive`: сам адрес или вложенный). Роли — объединение
        // ролей разделов из реестра, тот же список у маршрута и палитры.
        text: `${t('menu.allSettings')}`,
        to: PATHS.settings,
        match: PATHS.settings,
        roles: SETTINGS_HUB_ROLES,
        icon: Settings,
      },
      {
        text: `${t('menu.users')}`,
        to: PATHS.users,
        roles: [UserRole.ADMIN],
        icon: UserRound,
      },
      {
        text: `${t('menu.zoomRecordings')}`,
        to: PATHS.zoomDashboard,
        match: PATHS.zoom,
        roles: [UserRole.ADMIN, UserRole.ACCOUNTANT, UserRole.METHODIST],
        icon: Video,
      },
      {
        text: `${t('menu.mcpAccess')}`,
        to: PATHS.mcpAccess,
        // ТЗ 0023. Не `STUDENT_ROLES`: список обязан совпадать с
        // `MANAGEMENT_ROLES` бэкенда, а тот включает HR-менеджера. Пока
        // пункта не было, HR с выданным доступом не мог ни узнать адрес
        // эндпойнта, ни отозвать своё подключение — при том что подключиться
        // ему бэкенд позволял. ТЗ 0093: общая константа с карточкой хаба.
        roles: MCP_ACCESS_ROLES,
        icon: Bot,
      },
      {
        // ТЗ 0104: ИИ-агент ERP — прогоны, чат, база знаний, бюджет. Роли —
        // `AI_AGENT_ROLES` (только Admin), тот же список у маршрута и палитры.
        text: `${t('menu.aiAgents')}`,
        to: PATHS.aiAgents,
        roles: AI_AGENT_ROLES,
        icon: BotMessageSquare,
      },
      {
        // ТЗ 0118 B4: предложения в базу знаний — рецензенту категорий без
        // Admin (у Admin — вкладка «Propozycje» страницы «ИИ-агент»). Роли —
        // все роли ERP; виден по назначениям (`feature: 'kbReview'`). Знак —
        // тот же, что у вкладки и команды палитры.
        text: `${t('menu.aiKbReview')}`,
        to: PATHS.aiKbReview,
        roles: PROFILE_ROLES,
        feature: 'kbReview',
        icon: Lightbulb,
      },
    ],
  },
];

/**
 * Группы меню, видимые пользователю: в каждой — только пункты, открытые его
 * ролям (и флагу подписи); группа без единого такого пункта выпадает целиком.
 * Чистая функция — её проверяет тест ролей (`__tests__/menuStructure.test.tsx`).
 */
export const getVisibleMenu = (
  t: TFunction,
  userRoles: UserRole[],
  {
    esignEnabled,
    kbReviewer = false,
  }: { esignEnabled: boolean; kbReviewer?: boolean },
): IMenuGroup[] =>
  getMenuStructure(t).reduce((result: IMenuGroup[], group) => {
    const children = group.children.filter(
      (leaf) =>
        leaf.roles.some((role) => userRoles.includes(role)) &&
        (leaf.feature !== 'esign' || esignEnabled) &&
        (leaf.feature !== 'kbReview' || kbReviewer),
    );
    return children.length ? [...result, { ...group, children }] : result;
  }, []);

/**
 * ТЗ 0096 — какие пункты несут счётчики и сколько в них: Коммуникатор —
 * непрочитанные письма и Slack, Таск-трекер — просроченные задачи. Чистая
 * функция: нули не в счёт (значок без числа не рисуется).
 */
export interface IMenuCounters {
  mail: number;
  slack: number;
  overdue: number;
}

export const menuLeafCounters = (
  to: string,
  counters: IMenuCounters | null,
): Partial<IMenuCounters> | null => {
  if (!counters) return null;
  if (to === PATHS.communicator && (counters.mail || counters.slack)) {
    return { mail: counters.mail, slack: counters.slack };
  }
  if (to === PATHS.dashboard && counters.overdue) {
    return { overdue: counters.overdue };
  }
  return null;
};

/**
 * ТЗ 0118 B4 — сколько предложений ждёт рецензента (пункт очереди базы
 * знаний). Ноль — значка нет.
 */
export const menuKbPending = (
  to: string,
  reviewer: Pick<AiKbReviewerMe, 'pendingCount'> | null,
): number => (to === PATHS.aiKbReview && reviewer ? reviewer.pendingCount : 0);

/**
 * Значок «ждут разбора» у пункта очереди — тон «ждёт человека», та же
 * палитра `TAG_TONE_COLORS.gold`, что у счётчика вкладки «Propozycje»
 * (`proposals.styled` `CountBadge`).
 */
const KbPendingBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: ${TAG_TONE_COLORS.gold.bg};
  color: ${TAG_TONE_COLORS.gold.fg};
  font-size: 11px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
`;

const MOBILE_SHEET_ID = 'erp-mobile-menu';

export const Menu = memo(() => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const history = useHistory();
  const [menuItems, setMenuItems] = useState<ReactNode[] | null>([null]);
  const [{ user, loading }] = useUser();
  const { openPalette } = usePalette();
  const { enabled: esignEnabled } = useEsignAvailability();
  const { counters: comms } = useMyCommsCounters();
  // ТЗ 0118 B4: рецензент базы знаний — пункт очереди и счётчик.
  const { me: kbReviewer } = useAiKbReviewerMe(!user);
  const kbReviewerShown = aiKbShowsReviewEntry(kbReviewer);
  const menuCounters = useMemo<IMenuCounters | null>(
    () =>
      comms
        ? {
            mail: comms.mailUnread,
            slack: comms.slackUnread,
            overdue: comms.overdueTasks,
          }
        : null,
    [comms],
  );

  // ТЗ 0106: кнопка ИИ-чата — те же роли, что у маршрута и палитры.
  const canChat = ((user?.roles ?? []) as UserRole[]).some((role) =>
    AI_AGENT_ROLES.includes(role),
  );

  const logOut = useLogOut();
  const openProfile = useCallback(() => history.push(PATHS.profile), [history]);

  // Нативные title, не antd Tooltip: выпадающий список — hover-меню,
  // поповер поверх него закрывал бы соседние пункты.
  const badgeOf = useCallback(
    (to: string): ReactNode => {
      const kbPending = menuKbPending(to, kbReviewer);
      if (kbPending) {
        const label = t('aiKb.review.pendingBadge', {
          n: formatAiCount(kbPending),
        });
        return (
          <KbPendingBadge title={label} aria-label={label}>
            {formatAiCount(kbPending)}
          </KbPendingBadge>
        );
      }
      const leafCounters = menuLeafCounters(to, menuCounters);
      return leafCounters ? (
        <CommsCounters compact nativeTooltips {...leafCounters} />
      ) : null;
    },
    [t, kbReviewer, menuCounters],
  );

  const groupHasNew = useCallback(
    (group: IMenuGroup): boolean =>
      group.children.some(
        (leaf) =>
          Boolean(menuLeafCounters(leaf.to, menuCounters)) ||
          menuKbPending(leaf.to, kbReviewer) > 0,
      ),
    [menuCounters, kbReviewer],
  );

  const getMenuByRoles = useCallback(
    (userRoles: UserRole[]): ReactNode[] =>
      getVisibleMenu(t, userRoles, {
        esignEnabled,
        kbReviewer: kbReviewerShown,
      }).map((group) => {
        const visible = group.children;
        const isActive = visible.some((leaf) =>
          isPathActive(pathname, leaf.match ?? leaf.to),
        );
        // A lone visible leaf renders as a plain item with its own label.
        const single = visible.length === 1;
        const hasCounters = groupHasNew(group);

        return (
          <MenuItem
            key={group.text}
            to={visible[0].to}
            icon={single ? visible[0].icon : undefined}
            isActive={isActive}
            badge={single ? badgeOf(visible[0].to) : undefined}
            headDot={
              !single && hasCounters ? t('menu.counters.groupHasNew') : null
            }
            isMulti={
              single
                ? undefined
                : visible.map((leaf) => ({
                    children: leaf.text,
                    to: leaf.to,
                    icon: leaf.icon,
                    badge: badgeOf(leaf.to),
                  }))
            }
          >
            {single ? visible[0].text : group.text}
          </MenuItem>
        );
      }),
    [pathname, t, esignEnabled, kbReviewerShown, badgeOf, groupHasNew],
  );

  useEffect(() => {
    if (!loading && user?.roles && user.roles.length > 0) {
      setMenuItems(getMenuByRoles(user?.roles as UserRole[]));
    }
  }, [user, loading, getMenuByRoles]);

  // ТЗ 0121: телефон — своя шапка, лист меню и панель вкладок. Данные те
  // же: `getVisibleMenu` (роли, флаги) и счётчики шапки.
  const isMobile = useIsMobile();
  const notificationCenter = useNotificationCenter();
  const unreadNotifications = useNotificationUnreadCount();
  const [sheetOpen, setSheetOpen] = useState(false);
  const roles = useMemo(() => (user?.roles ?? []) as UserRole[], [user]);
  const visibleMenu = useMemo(
    () =>
      isMobile && roles.length
        ? getVisibleMenu(t, roles, {
            esignEnabled,
            kbReviewer: kbReviewerShown,
          })
        : [],
    [isMobile, roles, t, esignEnabled, kbReviewerShown],
  );
  const mobileTabs = useMemo(() => getMobileTabs(roles, visibleMenu), [
    roles,
    visibleMenu,
  ]);
  const tabCount = useCallback(
    (to: string): number => {
      const leafCounters = menuLeafCounters(to, menuCounters);
      const counted = leafCounters
        ? (leafCounters.mail ?? 0) +
          (leafCounters.slack ?? 0) +
          (leafCounters.overdue ?? 0)
        : 0;
      return counted + menuKbPending(to, kbReviewer);
    },
    [menuCounters, kbReviewer],
  );
  const openSheet = useCallback(() => setSheetOpen(true), []);
  const closeSheet = useCallback(() => setSheetOpen(false), []);

  // Переход закрывает лист (и при уходе с телефонной ширины — тоже).
  useEffect(() => {
    setSheetOpen(false);
  }, [pathname, isMobile]);

  if (isMobile) {
    const activeLeaf = findActiveLeaf(pathname, visibleMenu);
    return (
      <>
        <MobileHeader
          title={activeLeaf?.text ?? t('mobile.header.home')}
          menuOpen={sheetOpen}
          menuId={MOBILE_SHEET_ID}
          onOpenMenu={openSheet}
          onOpenPalette={openPalette}
          notifications={<NotificationCenter />}
          userFirstName={user?.firstName}
          userLastName={user?.lastName}
          userEmail={user?.email}
          onOpenProfile={openProfile}
        />
        <MobileMenuSheet
          id={MOBILE_SHEET_ID}
          open={sheetOpen}
          onClose={closeSheet}
          groups={visibleMenu}
          pathname={pathname}
          badgeOf={badgeOf}
          groupHasNew={groupHasNew}
          userFirstName={user?.firstName}
          userLastName={user?.lastName}
          userEmail={user?.email}
          profileTo={PATHS.profile}
          aiChatTo={canChat ? PATHS.aiChat : null}
          onExit={logOut}
        />
        <MobileTabBar
          tabs={mobileTabs}
          pathname={pathname}
          countOf={tabCount}
          notificationsOpen={notificationCenter.open}
          notificationsCount={unreadNotifications}
          onOpenNotifications={notificationCenter.openPanel}
          onCloseNotifications={notificationCenter.closePanel}
          menuOpen={sheetOpen}
          menuId={MOBILE_SHEET_ID}
          onOpenMenu={openSheet}
        />
      </>
    );
  }

  return (
    <MenuComponent
      onExit={logOut}
      onOpenPalette={openPalette}
      notifications={<NotificationCenter />}
      menuItems={menuItems || []}
      userFirstName={user?.firstName}
      userLastName={user?.lastName}
      userEmail={user?.email}
      onOpenProfile={openProfile}
      aiChatTo={canChat ? PATHS.aiChat : null}
      aiChatActive={isPathActive(pathname, PATHS.aiChat)}
    />
  );
});
